import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth, useTheme, api } from "../App";
import {
  Mail, Lock, User, ArrowLeft, Moon, Sun, Eye, EyeOff, Sparkles, Gift, AlertCircle,
} from "lucide-react";
import useFormValidation, { V } from "../hooks/useFormValidation";
import { FormField, ValidatedInput, FormErrorSummary } from "../components/FormComponents";

// ═══════════════════════════════════════════════════════════════
// Shared Google Sign-in renderer (no auth-form state inside)
// ═══════════════════════════════════════════════════════════════

const GSI_SRC = "https://accounts.google.com/gsi/client";
let _gsiPromise = null;

/**
 * Load Google Identity Services on demand.
 *
 * Aug 2026 — this script used to be a plain <script async defer> in
 * public/index.html, so all ~260KB of it downloaded on EVERY page view
 * including the homepage, where it is never used. It is only ever needed by
 * this component. `async defer` meant it did not block rendering, but on a
 * slow connection it still competed for bandwidth with the JS and CSS that DO
 * block the first paint.
 *
 * Memoised on a module-level promise so remounting the auth form (switching
 * between the login and signup tabs) reuses the one <script> element.
 */
function loadGsi() {
  if (_gsiPromise) return _gsiPromise;
  _gsiPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    // A previous mount may have inserted the tag but not finished loading.
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    const el = existing || document.createElement("script");
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", () => {
      // Let a later attempt retry rather than caching the failure forever.
      _gsiPromise = null;
      reject(new Error("Google Identity Services failed to load"));
    });
    if (!existing) {
      el.src = GSI_SRC;
      el.async = true;
      el.defer = true;
      document.head.appendChild(el);
    }
  });
  return _gsiPromise;
}

function GoogleSignIn({ mode, onSuccess }) {
  const btnRef = useRef(null);
  const { theme } = useTheme();
  useEffect(() => {
    const init = () => {
      if (!window.google?.accounts?.id || !btnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: onSuccess,
        auto_select: false,
      });
      btnRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(btnRef.current, {
        theme: theme === "dark" ? "filled_black" : "outline",
        size: "large",
        shape: "pill",
        text: mode === "login" ? "signin_with" : "signup_with",
        width: btnRef.current.offsetWidth || 400,
      });
    };
    let cancelled = false;
    if (window.google?.accounts?.id) {
      init();
    } else {
      loadGsi()
        .then(() => { if (!cancelled) init(); })
        .catch(() => { /* email/password sign-in still works without it */ });
    }
    return () => { cancelled = true; };
  }, [mode, theme, onSuccess]);
  return <div ref={btnRef} data-testid="google-signin-btn" style={{ minHeight: 44 }} />;
}

// ═══════════════════════════════════════════════════════════════
// LoginForm — completely isolated state
// ═══════════════════════════════════════════════════════════════
function LoginForm({ onGoogleSuccess, onSwitchMode, onAuthenticated, onForgotPassword, onMagicLink }) {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const rules = useMemo(() => ({
    email: [v => V.required(v, "Email"), V.email],
    password: [v => V.required(v, "Password"), V.minLength(6, "Password")],
  }), []);
  const { errors, touched, validate, setFieldTouched, registerRef, scrollToFirstError } = useFormValidation(rules);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
    if (touched[name]) setFieldTouched(name, value);
  };
  const handleBlur = (field) => setFieldTouched(field, formData[field]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate(formData)) { scrollToFirstError(); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: formData.email.trim(), password: formData.password,
      });
      onAuthenticated(data, "Welcome back!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign in failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>Welcome back</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-8">Sign in to continue to your dashboard</p>

      <div className="mb-6 md:mb-8"><GoogleSignIn mode="login" onSuccess={onGoogleSuccess} /></div>

      <div className="flex items-center gap-4 mb-6 md:mb-8">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <FormErrorSummary errors={errors} touched={touched} />

      <form onSubmit={submit} className="space-y-4 md:space-y-5" noValidate data-testid="login-form">
        <FormField label="Email" htmlFor="login-email" error={errors.email} touched={touched.email} required>
          <div className="relative" ref={registerRef("email")}>
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="login-email" name="email" type="email" placeholder="you@example.com"
              value={formData.email} onChange={handleChange}
              onBlurValidate={() => handleBlur("email")}
              error={errors.email} touched={touched.email}
              className="input-premium pl-11" autoComplete="email" inputMode="email"
              data-testid="email-input"
            />
          </div>
        </FormField>

        <FormField label="Password" htmlFor="login-password" error={errors.password} touched={touched.password} required>
          <div className="relative" ref={registerRef("password")}>
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="login-password" name="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={formData.password} onChange={handleChange}
              onBlurValidate={() => handleBlur("password")}
              error={errors.password} touched={touched.password}
              className="input-premium pl-11 pr-11" autoComplete="current-password"
              data-testid="password-input"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </FormField>

        <Button type="submit" size="lg" fullWidth loading={loading} disabled={loading} data-testid="auth-submit-btn">
          {loading ? "Signing in..." : "Sign In"}
        </Button>

        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-muted-foreground hover:text-primary hover:underline transition-colors"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
          <span className="text-muted-foreground/40 text-xs select-none">•</span>
          <button
            type="button"
            onClick={onMagicLink}
            className="text-sm text-primary hover:underline font-medium transition-colors"
            data-testid="magic-link-login-link"
          >
            Sign in with email (no password)
          </button>
        </div>
      </form>

      <p className="text-center mt-6 md:mt-8 text-sm text-muted-foreground">
        Don't have an account?{" "}
        <button onClick={onSwitchMode} className="text-primary hover:underline font-medium" data-testid="toggle-auth-mode-btn" type="button">
          Sign up
        </button>
      </p>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// SignupForm — completely isolated state
// ═══════════════════════════════════════════════════════════════
function SignupForm({ onGoogleSuccess, onSwitchMode, onCodeSent }) {
  const [formData, setFormData] = useState({ name: "", email: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const rules = useMemo(() => ({
    name: [v => V.required(v, "Name")],
    email: [v => V.required(v, "Email"), V.email],
    password: [v => V.required(v, "Password"), V.minLength(6, "Password")],
  }), []);
  const { errors, touched, validate, setFieldTouched, registerRef, scrollToFirstError } = useFormValidation(rules);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
    if (touched[name]) setFieldTouched(name, value);
  };
  const handleBlur = (field) => setFieldTouched(field, formData[field]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate(formData)) { scrollToFirstError(); return; }
    setLoading(true);
    try {
      // NO ACCOUNT IS CREATED HERE. The details are held server-side
      // and a code goes to the address; /auth/signup/verify is what
      // creates the account, once the code proves the address is real.
      await api.post("/auth/signup/start", {
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        referral_code: formData.referral_code.trim() || null,
      });
      onCodeSent(formData.email.trim());
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign up failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>Create account</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-8">Start automating your Instagram today</p>

      <div className="mb-6 md:mb-8"><GoogleSignIn mode="register" onSuccess={onGoogleSuccess} /></div>

      <div className="flex items-center gap-4 mb-6 md:mb-8">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <FormErrorSummary errors={errors} touched={touched} />

      <form onSubmit={submit} className="space-y-4 md:space-y-5" noValidate data-testid="signup-form">
        <FormField label="Name" htmlFor="signup-name" error={errors.name} touched={touched.name} required>
          <div className="relative" ref={registerRef("name")}>
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="signup-name" name="name" type="text" placeholder="Your name"
              value={formData.name} onChange={handleChange}
              onBlurValidate={() => handleBlur("name")}
              error={errors.name} touched={touched.name}
              className="input-premium pl-11" autoComplete="name"
              data-testid="name-input"
            />
          </div>
        </FormField>

        <FormField label="Referral Code" htmlFor="signup-referral" helper="Optional — enter a friend's code for bonus DMs">
          <div className="relative">
            <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="signup-referral" name="referral_code" type="text" placeholder="e.g. PIYUSH-A4K9"
              value={formData.referral_code} onChange={handleChange}
              className="input-premium pl-11 uppercase" autoComplete="off"
              data-testid="referral-code-input"
            />
          </div>
        </FormField>

        <FormField label="Email" htmlFor="signup-email" error={errors.email} touched={touched.email} required>
          <div className="relative" ref={registerRef("email")}>
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="signup-email" name="email" type="email" placeholder="you@example.com"
              value={formData.email} onChange={handleChange}
              onBlurValidate={() => handleBlur("email")}
              error={errors.email} touched={touched.email}
              className="input-premium pl-11" autoComplete="email" inputMode="email"
              data-testid="email-input"
            />
          </div>
        </FormField>

        <FormField label="Password" htmlFor="signup-password" error={errors.password} touched={touched.password} required helper="At least 6 characters">
          <div className="relative" ref={registerRef("password")}>
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="signup-password" name="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={formData.password} onChange={handleChange}
              onBlurValidate={() => handleBlur("password")}
              error={errors.password} touched={touched.password}
              className="input-premium pl-11 pr-11" autoComplete="new-password"
              data-testid="password-input"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </FormField>

        <Button type="submit" size="lg" fullWidth loading={loading} disabled={loading} data-testid="auth-submit-btn">
          {loading ? "Creating account..." : "Create Account"}
        </Button>
      </form>

      <p className="text-center mt-6 md:mt-8 text-sm text-muted-foreground">
        Already have an account?{" "}
        <button onClick={onSwitchMode} className="text-primary hover:underline font-medium" data-testid="toggle-auth-mode-btn" type="button">
          Sign in
        </button>
      </p>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// ForgotPasswordForm — step 1: request code
// ═══════════════════════════════════════════════════════════════
function ForgotPasswordForm({ onCodeSent, onBack }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const rules = useMemo(() => ({ email: [v => V.required(v, "Email"), V.email] }), []);
  const { errors, touched, validate, setFieldTouched, registerRef } = useFormValidation(rules);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate({ email })) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/request-code", { email: email.trim() });
      toast.success("If an account exists, a code has been sent to your email.");
      onCodeSent(email.trim());
    } catch {
      toast.error("Could not send code right now. Please try again.");
    }
    setLoading(false);
  };

  return (
    <>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors" data-testid="back-to-login-link">
        <ArrowLeft className="w-4 h-4" />Back to sign in
      </button>
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>Forgot password?</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-8">Enter your email and we'll send you a 6-digit verification code.</p>

      <FormErrorSummary errors={errors} touched={touched} />

      <form onSubmit={submit} className="space-y-4 md:space-y-5" noValidate data-testid="forgot-password-form">
        <FormField label="Email" htmlFor="forgot-email" error={errors.email} touched={touched.email} required>
          <div className="relative" ref={registerRef("email")}>
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="forgot-email" name="email" type="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onBlurValidate={() => setFieldTouched("email", email)}
              error={errors.email} touched={touched.email}
              className="input-premium pl-11" autoComplete="email" inputMode="email"
              data-testid="forgot-email-input" autoFocus
            />
          </div>
        </FormField>

        <Button type="submit" size="lg" fullWidth loading={loading} disabled={loading} data-testid="forgot-submit-btn">
          {loading ? "Sending..." : "Send Verification Code"}
        </Button>
      </form>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// VerifyCodeForm — step 2: enter 6-digit code
// ═══════════════════════════════════════════════════════════════
// Parameterised rather than copied: the signup OTP step needs exactly
// this box — six digits, paste, arrow/backspace nav, auto-submit on the
// sixth, resend cooldown — and a second copy would be the one that
// stops handling paste correctly. Defaults are the password-reset flow,
// so its existing call site is unchanged.
function VerifyCodeForm({
  email, onVerified, onBack, onResent,
  verifyPath = "/auth/forgot-password/verify-code",
  resendPath = "/auth/forgot-password/resend-code",
  buildBody = (addr, code) => ({ email: addr, code }),
  pickResult = (data) => data.reset_token,
  title = "Verify code",
  submitLabel = "Verify Code",
  backLabel = "Change email",
}) {
  const RESEND_SECS = 60;
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECS);
  const inputRefs = useRef([]);

  // Countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const focusIndex = (i) => {
    const el = inputRefs.current[Math.max(0, Math.min(5, i))];
    el?.focus();
    el?.select?.();
  };

  const onDigitChange = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setError("");
    setDigits(prev => {
      const next = [...prev];
      next[i] = d;
      return next;
    });
    if (d && i < 5) focusIndex(i + 1);
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) focusIndex(i - 1);
    if (e.key === "ArrowLeft") { e.preventDefault(); focusIndex(i - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); focusIndex(i + 1); }
  };

  const onPaste = (e) => {
    const raw = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!raw) return;
    e.preventDefault();
    const next = raw.split("").concat(Array(6 - raw.length).fill(""));
    setDigits(next);
    focusIndex(Math.min(5, raw.length));
  };

  const code = digits.join("");

  const submit = async (e) => {
    e?.preventDefault();
    if (loading) return;
    if (code.length !== 6) { setError("Enter all 6 digits"); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(verifyPath, buildBody(email, code));
      onVerified(pickResult(data));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid code. Try again.");
      setDigits(["", "", "", "", "", ""]);
      focusIndex(0);
    }
    setLoading(false);
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await api.post(resendPath, { email });
      toast.success("New code sent. Check your email.");
      setCooldown(RESEND_SECS);
      setDigits(["", "", "", "", "", ""]);
      setError("");
      focusIndex(0);
      onResent?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Could not resend code. Please try again.");
      }
    }
    setResending(false);
  };

  // Auto-submit when all 6 filled
  useEffect(() => {
    if (code.length === 6 && !loading) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors" data-testid="back-to-email-link">
        <ArrowLeft className="w-4 h-4" />{backLabel}
      </button>
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>{title}</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-6">
        Enter the 6-digit code we sent to <span className="font-medium text-foreground">{email}</span>.
      </p>

      <form onSubmit={submit} className="space-y-5" noValidate data-testid="verify-code-form">
        <div
          className="flex items-center gap-2 justify-center"
          data-testid="otp-input-row"
          onPaste={onPaste}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={1}
              value={d}
              onChange={(e) => onDigitChange(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              onFocus={(e) => e.target.select()}
              aria-label={`Digit ${i + 1}`}
              data-testid={`otp-digit-${i}`}
              className={`w-11 h-14 md:w-12 md:h-14 text-center text-xl md:text-2xl font-semibold rounded-xl border bg-background outline-none transition-all ${error ? "border-red-500 focus:border-red-500" : d ? "border-primary/50 focus:border-primary" : "border-border focus:border-primary/60"}`}
            />
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500 justify-center" data-testid="verify-code-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <Button type="submit" size="lg" fullWidth loading={loading} disabled={loading || code.length !== 6} data-testid="verify-submit-btn">
          {loading ? "Verifying..." : submitLabel}
        </Button>

        <div className="text-center text-sm text-muted-foreground">
          Didn't receive it?{" "}
          {cooldown > 0 ? (
            <span data-testid="resend-cooldown">Resend in {cooldown}s</span>
          ) : (
            <button type="button" onClick={resend} disabled={resending} className="text-primary hover:underline font-medium disabled:opacity-60" data-testid="resend-code-btn">
              {resending ? "Sending..." : "Resend code"}
            </button>
          )}
        </div>
      </form>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// ResetPasswordForm — step 3: set new password
// ═══════════════════════════════════════════════════════════════
function ResetPasswordForm({ email, resetToken, onReset }) {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const rules = useMemo(() => ({
    pwd: [v => V.required(v, "Password"), V.minLength(6, "Password")],
    confirm: [
      v => V.required(v, "Confirm password"),
      v => (v !== pwd ? "Passwords don't match" : null),
    ],
  }), [pwd]);
  const { errors, touched, validate, setFieldTouched, registerRef } = useFormValidation(rules);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate({ pwd, confirm })) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/reset-password", {
        email, reset_token: resetToken, new_password: pwd,
      });
      toast.success("Password reset successfully. Please sign in.");
      onReset();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reset password. Try again.");
    }
    setLoading(false);
  };

  return (
    <>
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>Reset password</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-8">
        Set a new password for <span className="font-medium text-foreground">{email}</span>.
      </p>

      <FormErrorSummary errors={errors} touched={touched} />

      <form onSubmit={submit} className="space-y-4 md:space-y-5" noValidate data-testid="reset-password-form">
        <FormField label="New password" htmlFor="reset-new" error={errors.pwd} touched={touched.pwd} required helper="At least 6 characters">
          <div className="relative" ref={registerRef("pwd")}>
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="reset-new" name="pwd" type={show ? "text" : "password"}
              placeholder="••••••••"
              value={pwd} onChange={(e) => setPwd(e.target.value)}
              onBlurValidate={() => setFieldTouched("pwd", pwd)}
              error={errors.pwd} touched={touched.pwd}
              className="input-premium pl-11 pr-11" autoComplete="new-password"
              data-testid="reset-new-input"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={show ? "Hide password" : "Show password"}>
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </FormField>

        <FormField label="Confirm password" htmlFor="reset-confirm" error={errors.confirm} touched={touched.confirm} required>
          <div className="relative" ref={registerRef("confirm")}>
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="reset-confirm" name="confirm" type={show ? "text" : "password"}
              placeholder="••••••••"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              onBlurValidate={() => setFieldTouched("confirm", confirm)}
              error={errors.confirm} touched={touched.confirm}
              className="input-premium pl-11" autoComplete="new-password"
              data-testid="reset-confirm-input"
            />
          </div>
        </FormField>

        <Button type="submit" size="lg" fullWidth loading={loading} disabled={loading} data-testid="reset-submit-btn">
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// MagicLinkRequestForm — step 1: passwordless sign-in, request 6-digit code
// ═══════════════════════════════════════════════════════════════
function MagicLinkRequestForm({ onCodeSent, onBack, initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);

  const rules = useMemo(() => ({ email: [v => V.required(v, "Email"), V.email] }), []);
  const { errors, touched, validate, setFieldTouched, registerRef } = useFormValidation(rules);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate({ email })) return;
    setLoading(true);
    try {
      await api.post("/auth/magic-link/request", { email: email.trim() });
      toast.success("If an account exists, a login code has been sent to your email.");
      onCodeSent(email.trim());
    } catch {
      toast.error("Could not send login code right now. Please try again.");
    }
    setLoading(false);
  };

  return (
    <>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors" data-testid="back-to-login-from-magic">
        <ArrowLeft className="w-4 h-4" />Back to sign in
      </button>
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>Sign in with email</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-8">Enter your email and we'll send you a 6-digit code to sign in — no password needed.</p>

      <FormErrorSummary errors={errors} touched={touched} />

      <form onSubmit={submit} className="space-y-4 md:space-y-5" noValidate data-testid="magic-link-request-form">
        <FormField label="Email" htmlFor="magic-email" error={errors.email} touched={touched.email} required>
          <div className="relative" ref={registerRef("email")}>
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <ValidatedInput
              id="magic-email" name="email" type="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onBlurValidate={() => setFieldTouched("email", email)}
              error={errors.email} touched={touched.email}
              className="input-premium pl-11" autoComplete="email" inputMode="email"
              data-testid="magic-email-input" autoFocus
            />
          </div>
        </FormField>

        <Button type="submit" size="lg" fullWidth loading={loading} disabled={loading} data-testid="magic-submit-btn">
          {loading ? "Sending..." : "Send Login Code"}
        </Button>
      </form>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// MagicLinkVerifyForm — step 2: enter 6-digit code → receive JWT
// ═══════════════════════════════════════════════════════════════
function MagicLinkVerifyForm({ email, onAuthenticated, onBack, initialCode = "" }) {
  const RESEND_SECS = 60;
  const seed = (initialCode || "").replace(/\D/g, "").slice(0, 6);
  const seedArr = seed.padEnd(6, " ").split("").map(c => (c === " " ? "" : c));
  const [digits, setDigits] = useState(seedArr);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECS);
  const inputRefs = useRef([]);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const focusIndex = (i) => {
    const el = inputRefs.current[Math.max(0, Math.min(5, i))];
    el?.focus();
    el?.select?.();
  };

  const onDigitChange = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setError("");
    setDigits(prev => {
      const next = [...prev];
      next[i] = d;
      return next;
    });
    if (d && i < 5) focusIndex(i + 1);
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) focusIndex(i - 1);
    if (e.key === "ArrowLeft") { e.preventDefault(); focusIndex(i - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); focusIndex(i + 1); }
  };

  const onPaste = (e) => {
    const raw = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!raw) return;
    e.preventDefault();
    const next = raw.split("").concat(Array(6 - raw.length).fill(""));
    setDigits(next);
    focusIndex(Math.min(5, raw.length));
  };

  const code = digits.join("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    if (code.length !== 6) { setError("Enter all 6 digits"); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/magic-link/verify", { email, code });
      onAuthenticated(data, "Signed in successfully");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid code. Try again.");
      setDigits(["", "", "", "", "", ""]);
      focusIndex(0);
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await api.post("/auth/magic-link/resend", { email });
      toast.success("New login code sent. Check your email.");
      setCooldown(RESEND_SECS);
      setDigits(["", "", "", "", "", ""]);
      setError("");
      focusIndex(0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Could not resend code. Please try again.");
      }
    }
    setResending(false);
  };

  // Auto-submit when all 6 filled. When seeded from a magic-link URL, fire exactly once.
  useEffect(() => {
    if (code.length === 6 && !loading && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      submit();
    }
    if (code.length < 6) autoSubmittedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors" data-testid="back-to-email-link-magic">
        <ArrowLeft className="w-4 h-4" />Change email
      </button>
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>Check your email</h1>
      <p className="text-sm md:text-base text-muted-foreground mb-6">
        Enter the 6-digit login code we sent to <span className="font-medium text-foreground">{email}</span>.
      </p>

      <form onSubmit={submit} className="space-y-5" noValidate data-testid="magic-verify-form">
        <div
          className="flex items-center gap-2 justify-center"
          data-testid="magic-otp-input-row"
          onPaste={onPaste}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={1}
              value={d}
              onChange={(e) => onDigitChange(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              onFocus={(e) => e.target.select()}
              aria-label={`Digit ${i + 1}`}
              data-testid={`magic-otp-digit-${i}`}
              className={`w-11 h-14 md:w-12 md:h-14 text-center text-xl md:text-2xl font-semibold rounded-xl border bg-background outline-none transition-all ${error ? "border-red-500 focus:border-red-500" : d ? "border-primary/50 focus:border-primary" : "border-border focus:border-primary/60"}`}
            />
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500 justify-center" data-testid="magic-verify-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <Button type="submit" size="lg" fullWidth loading={loading} disabled={loading || code.length !== 6} data-testid="magic-verify-submit-btn">
          {loading ? "Signing in..." : "Sign In"}
        </Button>

        <div className="text-center text-sm text-muted-foreground">
          Didn't receive it?{" "}
          {cooldown > 0 ? (
            <span data-testid="magic-resend-cooldown">Resend in {cooldown}s</span>
          ) : (
            <button type="button" onClick={resend} disabled={resending} className="text-primary hover:underline font-medium disabled:opacity-60" data-testid="magic-resend-btn">
              {resending ? "Sending..." : "Resend code"}
            </button>
          )}
        </div>
      </form>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// AuthPage — outer shell only. Mode derived from URL query param
// so browser back/forward/refresh always lands on a pristine form.
// ═══════════════════════════════════════════════════════════════
export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const modeParam = searchParams.get("mode");
  const mode =
    modeParam === "signup" ? "signup" :
    modeParam === "signup-verify" ? "signup-verify" :
    modeParam === "forgot" ? "forgot" :
    modeParam === "verify" ? "verify" :
    modeParam === "reset" ? "reset" :
    modeParam === "magic" ? "magic" :
    modeParam === "magic-verify" ? "magic-verify" :
    "login";
  const from = location.state?.from?.pathname || "/dashboard";

  // Forgot-flow transient state
  const [resetEmail, setResetEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  // Signup-OTP transient state. The mode lives in the URL and this does
  // not, so a reload on ?mode=signup-verify has no address to verify —
  // the render below falls back to the signup form rather than painting
  // an empty screen.
  const [signupEmail, setSignupEmail] = useState("");
  // Magic-link-flow transient state (seed from URL if magic-link was clicked from email)
  const urlEmail = (searchParams.get("email") || "").trim();
  const urlCode = (searchParams.get("code") || "").replace(/\D/g, "").slice(0, 6);
  const [magicEmail, setMagicEmail] = useState(mode === "magic-verify" ? urlEmail : "");
  const [magicSeedCode] = useState(mode === "magic-verify" ? urlCode : "");

  const setMode = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "login") params.delete("mode");
    else params.set("mode", next);
    setSearchParams(params, { replace: false });
  }, [searchParams, setSearchParams]);

  const handleAuthenticated = useCallback((data, successMsg) => {
    const { access_token, user } = data;
    login(user, access_token);
    toast.success(successMsg);
    navigate(from, { replace: true });
  }, [login, navigate, from]);

  const handleGoogleSuccess = useCallback(async (response) => {
    try {
      const result = await api.post("/auth/google", { credential: response.credential });
      handleAuthenticated(result.data, "Welcome to Tadlo!");
    } catch {
      toast.error("Google sign-in failed. Please try again.");
    }
  }, [handleAuthenticated]);

  const switchMode = useCallback(() => {
    setMode(mode === "login" ? "signup" : "login");
  }, [mode, setMode]);

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col lg:flex-row">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        <header className="p-4 md:p-5 flex items-center justify-between shrink-0">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />Back
          </button>
          <button onClick={toggleTheme} className="p-2.5 rounded-full hover:bg-accent transition-colors" data-testid="auth-theme-toggle">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </header>

        <div className="flex-1 flex items-start md:items-center justify-center p-4 md:p-6 overflow-y-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md pb-8">
            <div className="flex items-center gap-3 mb-8 md:mb-10">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl overflow-hidden shadow-lg">
                <img src="/tadlo-logo.jpeg" alt="Tadlo" className="w-full h-full object-cover" />
              </div>
              <span className="text-xl md:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Tadlo</span>
            </div>

            {/* Distinct keyed component instances — React mounts a fresh form on every mode switch. */}
            {mode === "login" && (
              <LoginForm
                key="login"
                onGoogleSuccess={handleGoogleSuccess}
                onSwitchMode={switchMode}
                onAuthenticated={handleAuthenticated}
                onForgotPassword={() => setMode("forgot")}
                onMagicLink={() => setMode("magic")}
              />
            )}
            {(mode === "signup" || (mode === "signup-verify" && !signupEmail)) && (
              <SignupForm
                key="signup"
                onGoogleSuccess={handleGoogleSuccess}
                onSwitchMode={switchMode}
                onCodeSent={(email) => { setSignupEmail(email); setMode("signup-verify"); }}
              />
            )}
            {mode === "signup-verify" && signupEmail && (
              <VerifyCodeForm
                key="signup-verify"
                email={signupEmail}
                verifyPath="/auth/signup/verify"
                resendPath="/auth/signup/resend"
                buildBody={(addr, code) => ({ email: addr, otp: code })}
                pickResult={(data) => data}
                title="Verify your email"
                submitLabel="Create account"
                backLabel="Back to sign up"
                onBack={() => { setSignupEmail(""); setMode("signup"); }}
                onVerified={(data) => { setSignupEmail(""); handleAuthenticated(data, "Account created!"); }}
              />
            )}
            {mode === "forgot" && (
              <ForgotPasswordForm
                key="forgot"
                onBack={() => setMode("login")}
                onCodeSent={(email) => { setResetEmail(email); setMode("verify"); }}
              />
            )}
            {mode === "verify" && resetEmail && (
              <VerifyCodeForm
                key={`verify-${resetEmail}`}
                email={resetEmail}
                onBack={() => setMode("forgot")}
                onVerified={(token) => { setResetToken(token); setMode("reset"); }}
              />
            )}
            {mode === "reset" && resetEmail && resetToken && (
              <ResetPasswordForm
                key="reset"
                email={resetEmail}
                resetToken={resetToken}
                onReset={() => { setResetEmail(""); setResetToken(""); setMode("login"); }}
              />
            )}
            {mode === "magic" && (
              <MagicLinkRequestForm
                key="magic"
                initialEmail={magicEmail}
                onBack={() => setMode("login")}
                onCodeSent={(email) => { setMagicEmail(email); setMode("magic-verify"); }}
              />
            )}
            {mode === "magic-verify" && magicEmail && (
              <MagicLinkVerifyForm
                key={`magic-verify-${magicEmail}`}
                email={magicEmail}
                initialCode={magicSeedCode}
                onBack={() => setMode("magic")}
                onAuthenticated={handleAuthenticated}
              />
            )}
            {/* Guard: user landed on verify/reset without flow context → restart from forgot */}
            {((mode === "verify" && !resetEmail) || (mode === "reset" && (!resetEmail || !resetToken))) && (
              <ForgotPasswordForm
                key="forgot-fallback"
                onBack={() => setMode("login")}
                onCodeSent={(email) => { setResetEmail(email); setMode("verify"); }}
              />
            )}
            {/* Guard: magic-verify without email in state → bounce back to request step */}
            {mode === "magic-verify" && !magicEmail && (
              <MagicLinkRequestForm
                key="magic-fallback"
                onBack={() => setMode("login")}
                onCodeSent={(email) => { setMagicEmail(email); setMode("magic-verify"); }}
              />
            )}
          </motion.div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-violet-500/5" />
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="relative z-10 max-w-md text-center">
          <div className="w-40 h-40 mx-auto mb-10 rounded-3xl overflow-hidden shadow-2xl">
            <img src="/tadlo-logo.jpeg" alt="Tadlo" className="w-full h-full object-cover" />
          </div>
          <h2 className="text-3xl font-light tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>Grow on autopilot</h2>
          <p className="text-muted-foreground text-lg leading-relaxed">Join thousands of creators using AI-powered automation to engage with their audience.</p>
          <div className="flex items-center justify-center gap-2 mt-8 text-primary"><Sparkles className="w-4 h-4" /><span className="text-sm font-medium">AI-powered replies included</span></div>
        </motion.div>
      </div>
    </div>
  );
}
